import { Box, Button, Typography, Paper, Grid, TextField, MenuItem, Checkbox, FormControlLabel } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { TraxxButton } from 'app/components';
import { API } from 'app/services';
import { makeStyles } from '@material-ui/core/styles';
import { useAsyncTask } from 'app/utils';
import { useRouter } from 'app/utils';
const useStyles = makeStyles((theme) => ({
  app: {
    padding: theme.spacing(3),
  },
  uploadBox: {
    border: '1px dashed #ccc',
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  preview: {
    width: '100%',
    maxHeight: 200,
    objectFit: 'contain',
    marginTop: 10,
  },
}));

const SelectDocument = ({ caseId, caseName, onBack, onNext, onDocument, contacts = [] }) => {
  const classes = useStyles();

  const router = useRouter();

  const [docType, setDocType] = useState('Passport');
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const [submitted, setSubmitted] = useState(false);

  const [runUpload, loading] = useAsyncTask('uploadDoc');
  const [faceDetection, setFaceDetection] = useState(false);

  const handleFile = (e, setter) => {
    const file = e.target.files[0];
    setter(file);
  };
  const identityDocs = ['NRIC / National ID', 'Driver’s License', 'Residence Permit'];

  const checkImageResolution = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        if (img.width < 500 || img.height < 500) {
          reject(new Error(`Image resolution too low (${img.width}x${img.height}). Minimum is 500x500.`));
        } else {
          resolve(true);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  };

  const handleSubmit = () => {
    setSubmitted(true);

    // 🔒 Validation
    if (!email || !firstName || !lastName) return;
    if (!front) return alert('Please upload the front document');
    if (docType === 'Identity' && !back) return alert('Please upload the back of Identity');

    runUpload(async () => {
      try {
        // 🔍 Resolution check
        await checkImageResolution(front);
        if (back) await checkImageResolution(back);

        const formData = new FormData();
        formData.append('case_id', caseId);
        formData.append('case_name', caseName);
        formData.append('document_type', docType);
        formData.append('email', email);
        formData.append('first_name', firstName);
        formData.append('last_name', lastName);
        formData.append('front', front);
        formData.append('face_detection', faceDetection ? 'true' : 'false');
        if (back) formData.append('back', back);

        const result = await API.Screening.upload_document(formData);

        // 🔥 VERY IMPORTANT: pass data to parent
        if (onDocument) {
          onDocument(result);
        }


        const id = result?.verification?.id || result?.uploadResult?.verification?.id;;

        if (onNext) {
          onNext();
        } else {
          router.history.push(`/documents/${id}/detail`);
        }
      } catch (error) {
        const errorMsg = error?.response?.data?.message || error?.message || 'Upload failed. Please try again.';

        alert(errorMsg);
      }
    });
  };

  return (
    <Box className={classes.app}>
      {/* HEADER */}
      <Box display="flex" alignItems="center" mb={3}>
        <Button onClick={onBack}>Previous</Button>
        <Box flexGrow={1} />
        <TraxxButton loading={loading} onSubmit={handleSubmit}>
          Submit
        </TraxxButton>
      </Box>

      <Paper elevation={2} style={{ padding: 24 }}>
        <Grid container spacing={3}>
          {/* RECIPIENT */}
          <Grid item xs={12}>
            <Typography variant="h6">Recipient</Typography>

            {contacts.length < 1 && (
              <Typography variant="body2" color="textSecondary">
                Enter screening details.
              </Typography>
            )}

            {/* CONTACT SELECT */}
            {contacts.length > 0 && (
              <Box mt={2}>
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  label="Auto-fill from previous screening"
                  value=""
                  onChange={(e) => {
                    const selected = contacts.find((c) => c.email === e.target.value);

                    if (selected) {
                      setEmail(selected.email);

                      const parts = selected.name.includes(',') ? selected.name.split(',') : selected.name.split(' ');

                      setFirstName(parts[0]?.trim() || '');
                      setLastName(parts[1]?.trim() || '');
                    }
                  }}>
                  <MenuItem value="">None</MenuItem>
                  {contacts.map((c, i) => (
                    <MenuItem key={i} value={c.email}>
                      {c.name} ({c.email})
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}

            {/* EMAIL */}
            <Box mt={2}>
              <TextField fullWidth variant="outlined" label="Email" value={email} onChange={(e) => setEmail(e.target.value)} error={submitted && !email} helperText={submitted && !email ? 'Please enter an email' : ''} />
            </Box>

            {/* NAME */}
            <Box mt={2}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField fullWidth variant="outlined" label="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} error={submitted && !firstName} />
                </Grid>

                <Grid item xs={6}>
                  <TextField fullWidth variant="outlined" label="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} error={submitted && !lastName} />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Paper elevation={2} style={{ padding: 24 }}>
            <Grid container spacing={3}>
              {/* DOCUMENT TYPE */}
              <Grid item xs={12}>
                <Typography variant="h6">Document Type</Typography>

                <Box mt={2}>
                  <ToggleButtonGroup value={docType} exclusive onChange={(_, val) => val && setDocType(val)}>
                    <ToggleButton value="Passport">Passport</ToggleButton>
                    <ToggleButton value="Identity">Identity</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Grid>

              {/* ACCEPTABLE DOCUMENTS */}
              {docType === 'Identity' && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Acceptable documents:
                  </Typography>

                  <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                    {identityDocs.map((doc, i) => (
                      <li key={i}>
                        <Typography variant="body2">{doc}</Typography>
                      </li>
                    ))}
                  </ul>
                </Grid>
              )}

              {/* FRONT */}
              <Grid item xs={12} md={6}>
                <Paper className={classes.uploadBox}>
                  <Typography>Upload Front {docType === 'Passport' ? '(Passport)' : '(ID Front)'}</Typography>

                  <Box mt={2}>
                    <input type="file" accept="image/*" onChange={(e) => handleFile(e, setFront)} />
                  </Box>

                  {front && <img src={URL.createObjectURL(front)} className={classes.preview} alt="front" />}
                </Paper>
              </Grid>

              {/* BACK */}
              {docType === 'Identity' && (
                <Grid item xs={12} md={6}>
                  <Paper className={classes.uploadBox}>
                    <Typography>Upload Back of Identity</Typography>

                    <Box mt={2}>
                      <input type="file" accept="image/*" onChange={(e) => handleFile(e, setBack)} />
                    </Box>

                    {back && <img src={URL.createObjectURL(back)} className={classes.preview} alt="back" />}
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Paper>
          {/* FACE DETECTION */}
          {/*
          <Grid item xs={12}>
            <Typography variant="h6">Face Detection</Typography>
            <Box mt={2}>
              <FormControlLabel control={<Checkbox checked={faceDetection} onChange={(e) => setFaceDetection(e.target.checked)} color="primary" />} label="Enable face detection (selfie match required)" />
            </Box>
          </Grid>
          */}
        </Grid>
      </Paper>
    </Box>
  );
};

export default SelectDocument;
